import { useState, useMemo, useEffect, useCallback, type MouseEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getProductDetail,
  type ProductWithSpecs,
  type ProductSpecOption,
} from '@/api/product';
import {
  addCommonGoods,
  addCommonType,
  getCommonTypeLists,
  type CommonUseType,
} from '@/api/common';
import { useCart } from '@/hooks/useCart';
import { clearDynamicBreadcrumbTitle, setDynamicBreadcrumbTitle } from '@/hooks/useDynamicBreadcrumb';
import Select from '@/components/base/Select';
import { toastError, toastSuccess } from '@/utils/message';

interface ViewSpec {
  id: string;
  name: string;
  price: number;
  unit: string;
  stock: number;
  supplier: string;
  image: string;
  sku: string;
  factory: string;
}

function mapSpec(spec: ProductSpecOption, fallbackImage: string): ViewSpec {
  const price = Number(spec.price ?? 0);
  const stock = typeof spec.stock === 'number' ? spec.stock : Number(spec.stock ?? 0);
  return {
    id: String(spec.id),
    name: String(spec.name || spec.sku || '默认规格').trim() || '默认规格',
    price: Number.isFinite(price) ? price : 0,
    unit: String(spec.unit || '件'),
    stock: Number.isFinite(stock) ? stock : 0,
    supplier: String(spec.supplier?.name || ''),
    image: String(spec.pic || fallbackImage || ''),
    sku: String(spec.sku || ''),
    factory: String(spec.factory || ''),
  };
}

function resolveProductHtml(goods?: ProductWithSpecs['goods'] | null): string {
  if (!goods) return '';
  const html = String(goods.html ?? '').trim();
  if (html) return html;

  // 兼容旧数据：information 可能是 HTML 或 JSON
  const information = String(goods.information ?? '').trim();
  if (!information) return '';
  try {
    const parsed = JSON.parse(information) as unknown;
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      return String(obj.html || obj.content || obj.description || '').trim();
    }
  } catch {
    return information;
  }
  return '';
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { items, addItem, updateQuantity } = useCart();

  const [quantity, setQuantity] = useState(1);
  const [selectedSpecId, setSelectedSpecId] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [detail, setDetail] = useState<ProductWithSpecs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCommonModal, setShowCommonModal] = useState(false);
  const [commonTypes, setCommonTypes] = useState<CommonUseType[]>([]);
  const [commonTypeId, setCommonTypeId] = useState('');
  const [commonSpecId, setCommonSpecId] = useState('');
  const [commonNumber, setCommonNumber] = useState(1);
  const [commonTypesLoading, setCommonTypesLoading] = useState(false);
  const [commonSubmitting, setCommonSubmitting] = useState(false);
  const [creatingType, setCreatingType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);

  useEffect(() => {
    if (!id) {
      setDetail(null);
      setLoading(false);
      setError('商品不存在');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    setSelectedSpecId('');
    setPreviewImage('');
    setQuantity(1);
    void (async () => {
      try {
        const res = await getProductDetail(id);
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        if (!ok || !res.data) {
          setDetail(null);
          setError(res.message || res.msg || '商品不存在或已下架');
          return;
        }
        setDetail(res.data);
      } catch {
        if (!cancelled) {
          setDetail(null);
          setError('商品加载失败，请稍后重试');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const goods = detail?.goods;
  const goodsId = String(detail?.goods_id || goods?.id || id || '');
  const productName = String(goods?.title || '未命名商品');
  const productCode = String(goods?.code || '');
  const coverImage =
    String(goods?.m_pic || '') ||
    (Array.isArray(goods?.albums) && goods?.albums[0]?.url ? String(goods.albums[0].url) : '');

  useEffect(() => {
    if (detail?.goods) {
      setDynamicBreadcrumbTitle(productName);
    } else {
      clearDynamicBreadcrumbTitle();
    }
    return () => clearDynamicBreadcrumbTitle();
  }, [detail, productName]);

  const specs = useMemo(
    () => (Array.isArray(detail?.specs) ? detail!.specs!.map((s) => mapSpec(s, coverImage)) : []),
    [detail, coverImage],
  );

  const activeSpec = useMemo(() => {
    if (!specs.length) return null;
    if (!selectedSpecId) return specs[0];
    return specs.find((s) => s.id === selectedSpecId) || specs[0];
  }, [specs, selectedSpecId]);

  const displayPrice = activeSpec?.price ?? 0;
  const displayStock = activeSpec?.stock ?? 0;
  const displayUnit = activeSpec?.unit || '件';
  const stockUnlimited = displayStock < 0;
  const canOrder = !!activeSpec && (stockUnlimited || displayStock > 0);
  const displaySupplier = activeSpec?.supplier || '';
  const displayImage = previewImage || activeSpec?.image || coverImage;
  const displayModel = activeSpec?.name || activeSpec?.factory || '-';
  const productHtml = resolveProductHtml(goods);

  const currentSpecId = selectedSpecId || specs[0]?.id || '';
  const inCart = items.find((i) => i.productId === goodsId && (i.specId || '') === currentSpecId);

  const stockLabel = !activeSpec
    ? '暂无规格'
    : stockUnlimited
      ? '库存充足'
      : displayStock > 50
        ? '库存充足'
        : displayStock > 0
          ? `仅剩 ${displayStock} ${displayUnit}`
          : '暂时缺货';
  const stockColor =
    !activeSpec
      ? 'text-foreground-400'
      : stockUnlimited || displayStock > 50
        ? 'text-accent-600'
        : displayStock > 0
          ? 'text-primary-600'
          : 'text-foreground-400';

  const clampQuantity = (value: number) => {
    const qty = Math.max(1, Math.floor(value) || 1);
    if (stockUnlimited) return qty;
    return Math.min(qty, Math.max(1, displayStock));
  };

  const handleAddToCart = (e?: MouseEvent<HTMLButtonElement>) => {
    if (!activeSpec || !goodsId) return;
    const anchor = e?.currentTarget;

    if (inCart) {
      const base = inCart.quantity >= 1 ? inCart.quantity : 0;
      updateQuantity(goodsId, base + quantity, { anchor });
    } else {
      for (let i = 0; i < quantity; i++) {
        addItem(
          {
            productId: goodsId,
            name: productName,
            price: activeSpec.price,
            unit: activeSpec.unit,
            image: activeSpec.image || coverImage,
            stock: activeSpec.stock,
            model: activeSpec.name,
            specId: activeSpec.id,
            specName: activeSpec.name,
          },
          { anchor },
        );
      }
    }
  };

  const handleBuyNow = (e: MouseEvent<HTMLButtonElement>) => {
    handleAddToCart(e);
    navigate('/cart');
  };

  const fetchCommonTypes = useCallback(async (preferId?: string) => {
    setCommonTypesLoading(true);
    try {
      const res = await getCommonTypeLists();
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        toastError(res.message || res.msg || '获取常购分类失败');
        setCommonTypes([]);
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      setCommonTypes(list);
      setCommonTypeId((prev) => {
        if (preferId && list.some((t) => String(t.id) === preferId)) return preferId;
        if (prev && list.some((t) => String(t.id) === prev)) return prev;
        return list[0] ? String(list[0].id) : '';
      });
    } catch {
      toastError('获取常购分类失败，请稍后重试');
      setCommonTypes([]);
    } finally {
      setCommonTypesLoading(false);
    }
  }, []);

  const openCommonModal = () => {
    if (!activeSpec || !goodsId) {
      toastError('请先选择商品规格');
      return;
    }
    setCommonSpecId(activeSpec.id);
    setCommonNumber(Math.max(1, quantity));
    setShowNewTypeInput(false);
    setNewTypeName('');
    setShowCommonModal(true);
    void fetchCommonTypes();
  };

  const closeCommonModal = () => {
    if (commonSubmitting || creatingType) return;
    setShowCommonModal(false);
  };

  const handleCreateCommonType = async () => {
    const name = newTypeName.trim();
    if (!name) {
      toastError('请输入分类名称');
      return;
    }
    if (creatingType) return;
    setCreatingType(true);
    try {
      const res = await addCommonType({ name });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        toastError(res.message || res.msg || '创建分类失败');
        return;
      }
      const newId = res.data?.id != null ? String(res.data.id) : undefined;
      toastSuccess(res.message || '添加常用分类成功');
      setShowNewTypeInput(false);
      setNewTypeName('');
      await fetchCommonTypes(newId);
    } catch {
      toastError('创建分类失败，请稍后重试');
    } finally {
      setCreatingType(false);
    }
  };

  const handleSubmitCommon = async () => {
    if (!goodsId) return;
    if (!commonTypeId) {
      toastError('请选择常购分类');
      return;
    }
    if (!commonSpecId) {
      toastError('请选择规格');
      return;
    }
    const number = Math.max(1, Math.floor(commonNumber) || 1);
    if (commonSubmitting) return;
    setCommonSubmitting(true);
    try {
      const res = await addCommonGoods({
        type_id: commonTypeId,
        goods_id: goodsId,
        spec_id: commonSpecId,
        number,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        toastError(res.message || res.msg || '加入常购单失败');
        return;
      }
      toastSuccess(res.message || '添加常用商品成功');
      setShowCommonModal(false);
    } catch {
      toastError('加入常购单失败，请稍后重试');
    } finally {
      setCommonSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 mb-3" />
        <p className="text-sm text-foreground-500">正在加载商品详情...</p>
      </div>
    );
  }

  if (!detail || error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-2xl text-foreground-400" />
        </div>
        <h2 className="text-lg font-semibold text-foreground-900 mb-2">商品未找到</h2>
        <p className="text-sm text-foreground-500 mb-4">{error || '该商品可能已下架或不存在'}</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm transition-colors"
        >
          返回首页
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background-100">
      <div className="p-4 md:p-6">
        <div className="bg-white rounded-xl border border-background-200 p-8 mb-8">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="w-full lg:w-[520px] shrink-0">
              <div className="aspect-square bg-background-100 rounded-lg overflow-hidden">
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt={productName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground-300">
                    <i className="ri-image-line text-4xl" />
                  </div>
                )}
              </div>
              {Array.isArray(goods?.albums) && goods!.albums!.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {goods!.albums!.map((album, idx) => {
                    const url = String(album.url || '');
                    if (!url) return null;
                    const active = displayImage === url;
                    return (
                      <button
                        key={String(album.id ?? idx)}
                        type="button"
                        onClick={() => setPreviewImage(url)}
                        className={`w-16 h-16 rounded-md overflow-hidden border shrink-0 transition-opacity ${
                          active
                            ? 'border-primary-500 opacity-100'
                            : 'border-background-200 opacity-80 hover:opacity-100'
                        }`}
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-semibold text-foreground-900 mb-2 leading-relaxed">
                {productName}
              </h1>
              {productCode && (
                <p className="text-sm text-foreground-500 mb-4">规格编码：{productCode}</p>
              )}

              <div className="bg-background-100 rounded-lg p-5 mb-5">
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-sm text-foreground-500">采购价：</span>
                  <span className="text-4xl font-bold text-primary-600">
                    ¥{displayPrice.toFixed(2)}
                  </span>
                  <span className="text-sm text-foreground-500">/{displayUnit}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex gap-1.5 min-w-0">
                    <span className="text-foreground-500 shrink-0">型号：</span>
                    <span className="text-foreground-700 truncate">{displayModel}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="text-foreground-500">单位：</span>
                    <span className="text-foreground-700">{displayUnit}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="text-foreground-500">库存：</span>
                    <span className={stockColor}>{stockLabel}</span>
                  </div>
                  <div className="flex gap-1.5 min-w-0">
                    <span className="text-foreground-500 shrink-0">供应商：</span>
                    <span className="text-foreground-700 truncate">{displaySupplier || '-'}</span>
                  </div>
                </div>
              </div>

              {specs.length > 0 && (
                <div className="mb-5">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">选择规格</h3>
                  <div className="flex flex-wrap gap-2">
                    {specs.map((spec) => {
                      const isActive = currentSpecId === spec.id;
                      const isOutOfStock = spec.stock >= 0 && spec.stock < 1;
                      return (
                        <button
                          key={spec.id}
                          type="button"
                          disabled={isOutOfStock}
                          onClick={() => {
                            setSelectedSpecId(spec.id);
                            setPreviewImage('');
                            setQuantity(1);
                          }}
                          className={`px-4 py-2.5 rounded-lg text-sm border transition-all whitespace-nowrap ${
                            isActive
                              ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                              : isOutOfStock
                                ? 'border-background-200 text-foreground-300 cursor-not-allowed'
                                : 'border-background-300 text-foreground-600 hover:border-primary-300'
                          }`}
                        >
                          {spec.name}
                          {isOutOfStock && <span className="ml-1 text-xs">（缺货）</span>}
                          <span className="ml-1 text-xs text-foreground-400">
                            ¥{spec.price.toFixed(2)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {canOrder && (
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-sm font-medium text-foreground-700">数量：</span>
                  <div className="flex items-center border border-background-300 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => clampQuantity(q - 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-background-100 transition-colors text-foreground-600"
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={stockUnlimited ? undefined : displayStock}
                      value={quantity}
                      onChange={(e) => setQuantity(clampQuantity(parseInt(e.target.value, 10) || 1))}
                      className="w-14 h-10 text-center text-sm outline-none border-x border-background-300"
                    />
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => clampQuantity(q + 1))}
                      className="w-10 h-10 flex items-center justify-center hover:bg-background-100 transition-colors text-foreground-600"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <div className="flex flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={!canOrder}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg border-2 border-primary-500 text-primary-600 font-medium hover:bg-primary-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap text-base"
                  >
                    <i className="ri-shopping-cart-line" />
                    {inCart ? `已加入购物车 (${inCart.quantity})` : '加入购物车'}
                  </button>
                  <button
                    type="button"
                    onClick={handleBuyNow}
                    disabled={!canOrder}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap text-base"
                  >
                    立即采购
                  </button>
                </div>
                <button
                  type="button"
                  onClick={openCommonModal}
                  disabled={!activeSpec}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-background-300 text-foreground-700 font-medium hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap text-base"
                >
                  <i className="ri-heart-3-line" />
                  加入常购单
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-background-200 pt-8">
            <h3 className="text-base font-semibold text-foreground-900 mb-4">商品详情</h3>
            {productHtml ? (
              <div
                className="product-detail-html text-base text-foreground-600 leading-relaxed max-w-none [&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:border-collapse [&_p]:mb-3 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: productHtml }}
              />
            ) : (
              <p className="text-sm text-foreground-400">暂无详细介绍</p>
            )}
          </div>
        </div>
      </div>

      {showCommonModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={closeCommonModal}
        >
          <div
            className="bg-background-50 rounded-xl w-full max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">加入常购单</h3>
              <button
                type="button"
                onClick={closeCommonModal}
                disabled={commonSubmitting || creatingType}
                className="w-7 h-7 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 hover:text-foreground-600 disabled:opacity-50"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <p className="text-xs text-foreground-500 mb-1">商品</p>
                <p className="text-sm font-medium text-foreground-900">{productName}</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-foreground-500">常购分类</label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewTypeInput((v) => !v);
                      setNewTypeName('');
                    }}
                    disabled={commonSubmitting || creatingType}
                    className="text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  >
                    <i className="ri-add-line" />
                    新建分类
                  </button>
                </div>
                {showNewTypeInput && (
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value.slice(0, 50))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleCreateCommonType();
                      }}
                      disabled={creatingType}
                      placeholder="输入新分类名称"
                      maxLength={50}
                      className="flex-1 px-3 py-2 rounded-lg border border-background-300 text-sm outline-none focus:border-primary-400 disabled:bg-background-100"
                    />
                    <button
                      type="button"
                      onClick={() => void handleCreateCommonType()}
                      disabled={creatingType || !newTypeName.trim()}
                      className="px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
                    >
                      {creatingType ? '创建中' : '创建'}
                    </button>
                  </div>
                )}
                {commonTypesLoading ? (
                  <p className="text-sm text-foreground-400 py-2">
                    <i className="ri-loader-4-line animate-spin mr-1" />
                    加载分类中...
                  </p>
                ) : commonTypes.length === 0 ? (
                  <p className="text-sm text-foreground-400 py-2">暂无分类，请先新建</p>
                ) : (
                  <Select
                    value={commonTypeId}
                    onChange={setCommonTypeId}
                    placeholder="请选择常购分类"
                    disabled={commonSubmitting}
                    options={commonTypes.map((t) => ({
                      label: String(t.name || '未命名分类'),
                      value: String(t.id),
                    }))}
                  />
                )}
              </div>

              <div>
                <label className="text-xs text-foreground-500 mb-1.5 block">规格</label>
                <Select
                  value={commonSpecId}
                  onChange={setCommonSpecId}
                  placeholder="请选择规格"
                  disabled={commonSubmitting || specs.length === 0}
                  options={specs.map((s) => ({
                    label: `${s.name} · ¥${s.price.toFixed(2)}/${s.unit}`,
                    value: s.id,
                    disabled: s.stock >= 0 && s.stock < 1,
                  }))}
                />
              </div>

              <div>
                <label className="text-xs text-foreground-500 mb-1.5 block">常购数量</label>
                <div className="flex items-center border border-background-300 rounded-lg overflow-hidden w-fit">
                  <button
                    type="button"
                    onClick={() => setCommonNumber((n) => Math.max(1, n - 1))}
                    disabled={commonSubmitting}
                    className="w-10 h-10 flex items-center justify-center hover:bg-background-100 text-foreground-600 disabled:opacity-50"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={commonNumber}
                    disabled={commonSubmitting}
                    onChange={(e) =>
                      setCommonNumber(Math.max(1, Math.floor(Number(e.target.value)) || 1))
                    }
                    className="w-14 h-10 text-center text-sm outline-none border-x border-background-300 disabled:bg-background-100"
                  />
                  <button
                    type="button"
                    onClick={() => setCommonNumber((n) => n + 1)}
                    disabled={commonSubmitting}
                    className="w-10 h-10 flex items-center justify-center hover:bg-background-100 text-foreground-600 disabled:opacity-50"
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-background-200 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => navigate('/frequent-purchase')}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                我的常购单
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeCommonModal}
                  disabled={commonSubmitting || creatingType}
                  className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleSubmitCommon()}
                  disabled={
                    commonSubmitting ||
                    creatingType ||
                    !commonTypeId ||
                    !commonSpecId ||
                    commonTypesLoading
                  }
                  className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {commonSubmitting ? '提交中...' : '确定'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
